'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import styles from './CustomPlayButton.module.scss'

import { useVideoManager } from '@/contexts/VideoManagerContext'


export interface CustomPlayButtonProps {
  vimeoId: string
  onPlayStateChange?: (isPlaying: boolean) => void
  className?: string
}

declare global {
  interface Window {
    Vimeo: {
      Player: new (iframe: HTMLIFrameElement, options?: Record<string, unknown>) => VimeoPlayer
    }
  }
}

interface VimeoPlayer {
  play(): Promise<void>
  pause(): Promise<void>
  getCurrentTime(): Promise<number>
  getDuration(): Promise<number>
  setCurrentTime(seconds: number): Promise<number>
  setVolume(volume: number): Promise<number>
  getVolume(): Promise<number>
  on(event: string, callback: (data?: unknown) => void): void
  off(event: string, callback: (data?: unknown) => void): void
  ready(): Promise<void>
  element: HTMLIFrameElement
}

export const CustomPlayButton: React.FC<CustomPlayButtonProps> = ({
  vimeoId,
  onPlayStateChange,
  className = ''
}) => {
  const videoManager = useVideoManager()
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPlayButton, setShowPlayButton] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showControls, setShowControls] = useState(false)
  const [isFirstPlay, setIsFirstPlay] = useState(true)
  
  const playerRef = useRef<VimeoPlayer | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const isVimeoAPILoadedRef = useRef(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const progressUpdateInterval = useRef<NodeJS.Timeout | null>(null)
  const controlsHideTimeout = useRef<NodeJS.Timeout | null>(null)
  
  // Generate unique ID for this player (using a stable ID to avoid hydration issues)
  const [playerId] = useState(() => {
    // Use a deterministic ID based on vimeoId to ensure server/client consistency
    let hash = 0
    const str = `video-${vimeoId}`
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return `video-${vimeoId}-${Math.abs(hash).toString(36).substr(0, 9)}`
  })
  const playerIdValue = playerId

  // Create stable player control functions for video manager
  const playVideoRef = useRef<() => Promise<void>>(async () => {
    if (!playerRef.current) return
    try {
      await playerRef.current.play()
    } catch (error) {
      console.warn('Failed to play video:', error)
    }
  })

  const pauseVideoRef = useRef<() => Promise<void>>(async () => {
    if (!playerRef.current) return
    try {
      await playerRef.current.pause()
    } catch (error) {
      console.warn('Failed to pause video:', error)
    }
  })

  // Load Vimeo Player API
  const loadVimeoAPI = (): Promise<void> => {
    return new Promise((resolve) => {
      if (isVimeoAPILoadedRef.current || window.Vimeo) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://player.vimeo.com/api/player.js'
      script.onload = () => {
        isVimeoAPILoadedRef.current = true
        resolve()
      }
      script.onerror = () => {
        console.warn('Failed to load Vimeo Player API')
        resolve() // Still resolve to prevent hanging
      }
      document.head.appendChild(script)
    })
  }

  // Update progress bar
  const updateProgress = useCallback(async () => {
    if (!playerRef.current) return
    
    try {
      const current = await playerRef.current.getCurrentTime()
      setCurrentTime(current)
    } catch {
      // Ignore errors during progress updates
    }
  }, [])

  // Start progress updates
  const startProgressUpdates = useCallback(() => {
    if (progressUpdateInterval.current) return
    
    progressUpdateInterval.current = setInterval(updateProgress, 100)
  }, [updateProgress])

  // Stop progress updates
  const stopProgressUpdates = useCallback(() => {
    if (progressUpdateInterval.current) {
      clearInterval(progressUpdateInterval.current)
      progressUpdateInterval.current = null
    }
  }, [])

  // Show controls temporarily
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true)
    
    // Clear existing timeout
    if (controlsHideTimeout.current) {
      clearTimeout(controlsHideTimeout.current)
    }
    
    // Hide controls after 3 seconds of inactivity
    controlsHideTimeout.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }, [isPlaying])

  // Intersection observer for scroll-based pausing
  useEffect(() => {
    if (!wrapperRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const isInView = entry.isIntersecting
        
        // If video is playing and goes out of view, pause it
        if (isPlaying && !isInView && pauseVideoRef.current) {
          pauseVideoRef.current()
        }
      },
      {
        threshold: 0.1, // Pause when less than 10% of video is visible
        rootMargin: '-50px 0px' // Add some margin to trigger earlier
      }
    )

    observer.observe(wrapperRef.current)
    
    return () => {
      observer.disconnect()
    }
  }, [isPlaying])



  // Enable audio for first play (unmute the video)
  const enableMobileAudio = useCallback(async () => {
    if (!playerRef.current) return
    
    try {
      // On mobile, videos start muted. Unmute them on user interaction.
      await playerRef.current.setVolume(1)
      
      // Small delay to ensure volume change takes effect
      await new Promise(resolve => setTimeout(resolve, 100))
    } catch {
      // Ignore volume setting errors
    }
  }, [])

  // Initialize Vimeo Player
  const initializePlayer = useCallback(async () => {
    if (!iframeRef.current || !vimeoId || playerRef.current) return

    try {
      await loadVimeoAPI()
      
      if (!window.Vimeo) {
        console.warn('Vimeo API not available')
        return
      }

      const player = new window.Vimeo.Player(iframeRef.current)
      playerRef.current = player

      // Set up event listeners
      const handlePlay = () => {
        setIsPlaying(true)
        setShowPlayButton(false)
        setIsLoading(false)
        setIsFirstPlay(false) // Mark that we've played at least once
        startProgressUpdates()
        videoManager.notifyPlay(playerIdValue)
        onPlayStateChange?.(true)
      }

      const handlePause = () => {
        setIsPlaying(false)
        setShowPlayButton(true) // Show play button when paused
        setShowControls(true) // Show controls when paused
        stopProgressUpdates()
        videoManager.notifyPause(playerIdValue)
        onPlayStateChange?.(false)
      }

      const handleEnded = () => {
        setIsPlaying(false)
        setShowPlayButton(true)
        setShowControls(false)
        setCurrentTime(0)
        stopProgressUpdates()
        videoManager.notifyPause(playerIdValue)
        onPlayStateChange?.(false)
      }

      const handleLoadedMetadata = async () => {
        try {
          const videoDuration = await player.getDuration()
          setDuration(videoDuration)
        } catch {
          // Ignore duration errors
        }
      }

      player.on('play', handlePlay)
      player.on('pause', handlePause)
      player.on('ended', handleEnded)
      player.on('loadedmetadata', handleLoadedMetadata)

      await player.ready()
      
      // Register this player with the video manager
      videoManager.registerPlayer(playerIdValue, {
        id: playerIdValue,
        play: playVideoRef.current!,
        pause: pauseVideoRef.current!,
        isPlaying: false
      })
      
      // Get initial duration
      handleLoadedMetadata()
    } catch (error) {
      console.warn('Failed to initialize Vimeo player:', error)
    }
  }, [vimeoId, videoManager, playerIdValue, startProgressUpdates, onPlayStateChange, stopProgressUpdates])

  // Handle play button click
  const handlePlayClick = async () => {
    if (isLoading || !playerRef.current) return

    setIsLoading(true)
    
    try {
      // Pause all other videos before playing this one
      videoManager.pauseAllExcept(playerIdValue)
      
      // For first play on mobile, ensure audio is enabled
      if (isFirstPlay) {
        await enableMobileAudio()
      }
      
      await playerRef.current.play()
    } catch (error) {
      console.warn('Failed to play video:', error)
      setIsLoading(false)
    }
  }

  // Handle video area click (pause only - since play is handled by play button)
  const handleVideoClick = async () => {
    if (!playerRef.current || !isPlaying) return

    showControlsTemporarily()

    try {
      await playerRef.current.pause()
    } catch (error) {
      console.warn('Failed to pause video:', error)
    }
  }

  // Handle progress bar click (seeking)
  const handleProgressClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current || !duration) return

    const rect = event.currentTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = clickX / rect.width
    const seekTime = percentage * duration

    try {
      await playerRef.current.setCurrentTime(seekTime)
      setCurrentTime(seekTime)
    } catch (error) {
      console.warn('Failed to seek video:', error)
    }
  }

  // Initialize player when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      // Find the iframe in the DOM (it should be a sibling or nearby element)
      const findVimeoIframe = () => {
        const iframes = document.querySelectorAll('iframe[src*="vimeo.com"]')
        for (const iframe of iframes) {
          const src = iframe.getAttribute('src')
          if (src && src.includes(vimeoId)) {
            return iframe as HTMLIFrameElement
          }
        }
        return null
      }

      const iframe = findVimeoIframe()
      if (iframe) {
        iframeRef.current = iframe
        initializePlayer()
      }
    }, 100) // Small delay to ensure iframe is rendered

    // Cleanup function
    return () => {
      clearTimeout(timer)
      
      // Stop progress updates
      if (progressUpdateInterval.current) {
        clearInterval(progressUpdateInterval.current)
        progressUpdateInterval.current = null
      }
      
      if (controlsHideTimeout.current) {
        clearTimeout(controlsHideTimeout.current)
      }
      
      // Unregister from video manager
      videoManager.unregisterPlayer(playerIdValue)
      
      if (playerRef.current) {
        try {
          playerRef.current.off('play', () => {})
          playerRef.current.off('pause', () => {})
          playerRef.current.off('ended', () => {})
          playerRef.current.off('loadedmetadata', () => {})
        } catch {
          // Ignore cleanup errors
        }
        playerRef.current = null
      }
    }
  }, [vimeoId, initializePlayer, videoManager, playerIdValue])

  if (!vimeoId) return null

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div 
      ref={wrapperRef}
      className={`${styles.customVideoControls} ${className} ${!isPlaying ? styles.showingPlayButton : styles.videoPlaying}`}
      onMouseMove={showControlsTemporarily}
    >
      {/* Video Click Area - Always present, behind play button */}
      <div 
        className={styles.videoClickArea}
        onClick={!isPlaying ? handlePlayClick : handleVideoClick}
        onTouchEnd={(e) => {
          e.preventDefault()
          if (showPlayButton || !isPlaying) {
            handlePlayClick()
          } else {
            handleVideoClick()
          }
        }}
        role="button"
        tabIndex={-1} // Remove from tab order since play button handles focus when visible
        aria-label={(showPlayButton || !isPlaying) ? "Play video" : "Pause video"}
      />

      {/* Play Button Visual Overlay - Interactive when play button is visible */}
      {!isPlaying && (
        <div 
          className={styles.playButtonOverlay}
          onClick={handlePlayClick}
          onTouchEnd={(e) => {
            e.preventDefault()
            handlePlayClick()
          }}
          role="button"
          tabIndex={0}
          aria-label="Play video"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handlePlayClick()
            }
          }}
        >
          <div className={styles.playButton__circle}>
            <svg 
              className={styles.playButton__icon}
              xmlns="http://www.w3.org/2000/svg" 
              width="25" 
              height="28" 
              viewBox="0 0 25 28" 
              fill="none"
            >
              <path 
                d="M23 10.908C25.6667 12.4476 25.6667 16.2966 23 17.8362L6.5 27.3624C3.83334 28.902 0.499999 26.9776 0.499999 23.8983L0.5 4.8458C0.5 1.7666 3.83333 -0.157913 6.5 1.38169L23 10.908Z" 
                fill="var(--color-primary)"
              />
            </svg>
          </div>
          {isLoading && <div className={styles.playButton__loading} />}
        </div>
      )}

      {/* Custom Controls */}
      {!showPlayButton && (!isPlaying || showControls) && (
        <div className={styles.customControls}>
          {/* Progress Bar */}
          <div 
            className={styles.progressBar}
            onClick={handleProgressClick}
            role="progressbar"
            aria-valuenow={currentTime}
            aria-valuemin={0}
            aria-valuemax={duration}
          >
            <div className={styles.progressBar__track}>
              <div 
                className={styles.progressBar__fill}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
